import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { DmsSearchResult, FileUploadState, FileVersionByNameI, IrisDMSFile, IrisDMSFileI } from '../models/IrisDMSFile';
import { muteFirst } from '@iris/common/utils/ngrx.utils';
import { IrisFilesService } from '../services/files.service';
import { IrisLanguageService } from '@iris/common/services/utils/language.service';
import { IrisDmsFilterService } from '@iris/common/modules/dms/services/dms-filter.service';
import { IrisDMSTagI } from '@iris/common/modules/dms/models/IrisDMSTag';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisDMSFolder, IrisDmsRecentFilesFolder, IrisDmsRootFolder } from '../models/IrisDMSFolder';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { ActivatedRoute, Router, UrlCreationOptions } from '@angular/router';
import { toggleSelectedFileQueryParam } from '@iris/common/utils/router.utils';
import { FileItem } from 'ng2-file-upload';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { IrisPageChangedEvent } from '../../grid/ag-grid/models/grid-pagination';
import { isEmpty, isNumber } from 'lodash';
import { IrisDmsPermissionsSandbox } from './dms-permissions.sandbox';
import { LatLngLiteral } from 'leaflet';
import { IrisDmsSidebarStore } from '../store/dms-sidebar.signal-store';
import { IrisDmsBulkActionsService } from '../services/dms-bulk-actions.service';
import { IRIS_ACTIVE_SIDEBAR_FILTER } from '@iris/modules/module-filters-v2/tokens';
import { IrisActiveFilterService } from '@iris/modules/module-filters-v2/active-filter';
import { IrisFileFilterEntityI } from '../models/IrisDmsFileFilterEntity';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IrisContentViewType } from '../models/IrisDmsViewType';
import { IrisDmsGlobalStore } from '../store/dms-global.signal-store';
import { IrisDmsContentStore } from '../store/dms-content.signal-store';
import { IrisDMSFolderOrFile } from '@iris/modules/dms/utils/file.utils';
import { IrisDmsSettingsSandbox } from './dms-settings.sandbox';
import { distinctUntilPropertyChanged } from '@iris/common/utils/rxjs.utils';
import { IrisQueryParams, IrisQueryParamsBuilder, IrisQueryParamsOrderBy } from '@iris/api-query';
import { IrisSecurityService } from '../../security/services/security.service';
import { IrisDmsModalFilesStore } from '../store/modal-files.signal-store';
import { IrisDmsRoutingService } from '../services/dms-routing.service';

@Injectable({ providedIn: 'root' })
export class IrisDmsFilesSandbox {
  private readonly foldersService = inject(IrisFoldersService);
  private readonly filesService = inject(IrisFilesService);
  private readonly dmsFilterService = inject(IrisDmsFilterService);
  private readonly irisLanguageService = inject(IrisLanguageService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly dmsSettingsSandbox = inject(IrisDmsSettingsSandbox);
  private readonly securityService = inject(IrisSecurityService);
  private readonly dmsBulkActionsService = inject(IrisDmsBulkActionsService);
  private readonly dmsRoutingService = inject(IrisDmsRoutingService);
  private readonly injector = inject(Injector);
  private readonly activeSidebarFilterService = inject<IrisActiveFilterService>(IRIS_ACTIVE_SIDEBAR_FILTER, { optional: true });

  private readonly dmsSidebarStore = inject(IrisDmsSidebarStore);
  private readonly dmsGlobalStore = inject(IrisDmsGlobalStore);
  private readonly dmsContentStore = inject(IrisDmsContentStore);
  private readonly dmsModalFilesStore = inject(IrisDmsModalFilesStore);

  readonly contentTotal = this.dmsContentStore.paginationTotal;

  readonly filterQueryParams$ = of(null).pipe(
    filter(() => !!this.activeSidebarFilterService?.filterQueryParams$),
    switchMap(() => this.activeSidebarFilterService?.filterQueryParams$),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  readonly filterValues$ = of(null).pipe(
    filter(() => !!this.activeSidebarFilterService?.filterValues$),
    switchMap(() => this.activeSidebarFilterService?.filterValues$),
  );

  readonly contentPagePagination = this.dmsContentStore.pagination;
  readonly contentPagePagination$ = toObservable(this.contentPagePagination);

  readonly requestContent$ = this.contentPagePagination$.pipe(
    switchMap((pagination) => {
      return this.dmsFoldersSandbox.selectedFolderId$.pipe(
        filter(Boolean),
        switchMap(selectedFolderId => this.dmsPermissionsSandbox.getUserAvailableActions$(selectedFolderId)),
        filter(actions => !!actions.length),
        take(1),
        map(() => pagination),
      );
    }),
    filter(pagination => {
      const selectedFolderId = this.dmsFoldersSandbox.selectedFolderId();
      const contentTotal = this.contentTotal();
      return !!selectedFolderId
        && selectedFolderId !== IrisDmsRecentFilesFolder.id
        && selectedFolderId !== IrisDmsRootFolder.id
        && !this.dmsSidebarStore.isFileEdit()
        && (contentTotal === null || pagination.offset <= contentTotal);
    }),
    switchMap(({ offset, limit, orderBy }) => {
      const selectedFolderId = this.dmsFoldersSandbox.selectedFolderId();
      const contentView = this.dmsGlobalStore.contentViewType();
      if (offset === 0 || contentView !== IrisContentViewType.Tile) {
        this.contentLoadingStart();
      }
      return this.foldersService.getContentPage({
        folderId: selectedFolderId,
        offset,
        limit,
        orderBy,
        excludeHistory: true,
      }).pipe(
        take(1),
        tap((contentPage) => {
          const {
            files,
            folders,
            count,
          } = contentPage;

          this.clearFilesMap(files);

          this.contentLoadingEnd();

          if (isNumber(count)) {
            this.dmsContentStore.setPaginationTotal(count);

            if (!folders.length && !files.length && count > 0) {
              this.setContentPagePagination({
                page: (offset / limit) - 1,
                pageSize: limit,
                orderBy,
              });
            }
          }
        }),
        tap(contentPage => {
          const entities = [
            ...contentPage.folders.map(folder => new IrisDMSFolder(folder)),
            ...contentPage.files.map(file => new IrisDMSFile(file)),
          ];
          if (contentView === IrisContentViewType.Tile && offset > 0) {
            this.dmsContentStore.setEntities(entities);
          } else {
            this.dmsContentStore.setAllEntities(entities);
          }
        }),
        catchError((err) => {
          this.contentLoadingEnd();
          return of(err);
        }),
        takeUntil(
          this.dmsFoldersSandbox.wrongRequestedFolderId$.pipe(
            filter((wrongRequestedFolderId) => wrongRequestedFolderId === selectedFolderId),
          ),
        ),
      );
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly requestFilterFiles$ = this.filterQueryParams$
    .pipe(
      debounceTime(100),
      distinctUntilPropertyChanged(),
      switchMap(fileFilter => {
        this.resetContentWithCurrentPagination();
        if (isEmpty(fileFilter)) {
          return EMPTY;
        }
        this.dmsFoldersSandbox.resetSelectedFolderId();
        return this.contentPagePagination$.pipe(
          distinctUntilChanged(),
          filter((pagination) => pagination.offset + pagination.limit > this.dmsContentStore.entities().length),
          map((pagination) => ({ fileFilter, pagination })),
        );
      }),
      switchMap(({ fileFilter, pagination }) => {
        const { offset, limit, orderBy } = pagination;
        const contentView = this.dmsGlobalStore.contentViewType();
        if (offset === 0 || contentView !== IrisContentViewType.Tile) {
          this.contentLoadingStart();
        }
        this.clearFilesMap();
        return this.searchFilteredFiles(fileFilter, offset, limit + 1, orderBy)
          .pipe(
            tap(({ searchHits }) => {
              const loadedItemsCount = offset + searchHits.length;
              this.dmsContentStore.setPaginationTotal(Math.max(this.contentTotal() ?? 0, loadedItemsCount));
            }),
            map(({ searchHits }) => ({ files: searchHits, offset })),
            map(({ files, offset }) => {
              this.contentLoadingEnd();
              const entities = files.map(file => new IrisDMSFile(file));
              if (offset > 0) {
                this.dmsContentStore.setEntities(entities);
              } else {
                this.dmsContentStore.setAllEntities(entities);
              }
            }),
            catchError(() => {
              this.contentLoadingEnd();
              this.notifyElasticUnavailable();
              return of([]);
            }),
          );
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
    );

  readonly requestRecentFiles$ = this.dmsFoldersSandbox.selectedFolderId$.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    filter(() => this.dmsFoldersSandbox.isRecentFilesFolderSelected()),
    switchMap(() => this.globalSandbox.currentProjectId$),
    switchMap(projectId => {
      this.clearFilesMap();
      if (!projectId) { return of([]); }
      this.contentLoadingStart();
      return this.filesService.getProjectRecentFiles(projectId).pipe(
        catchError(() => of([])),
      );
    }),
    tap(entities => {
      this.contentLoadingEnd();
      this.dmsContentStore.setPaginationTotal(entities.length);
      this.dmsContentStore.setAllEntities(entities as IrisDMSFolderOrFile[]);
    }),
    catchError(() => {
      this.contentLoadingEnd();
      return of([]);
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly entities = this.dmsContentStore.entities;
  readonly entites$ = toObservable(this.entities);

  readonly content$ = muteFirst(
    this.requestContent$.pipe(startWith(null as unknown[])),
    this.entites$,
  ).pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly files = this.dmsContentStore.files;
  readonly files$ = toObservable(this.files);

  readonly filterFiles$ = muteFirst(
    this.requestFilterFiles$.pipe(startWith(null as unknown[])),
    this.entites$,
  ).pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly filesIds = this.dmsContentStore.filesIds;
  readonly filesIds$ = toObservable(this.filesIds);

  readonly recentFiles$ = muteFirst(
    this.requestRecentFiles$.pipe(startWith(null as unknown[])),
    this.entites$,
  ).pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly hasContentNextPage = this.dmsContentStore.hasNextPage;

  readonly selectedFiles = this.dmsContentStore.selectedFiles;

  readonly selectedFolders = this.dmsContentStore.selectedFolders;
  readonly selectedFolders$ = toObservable(this.selectedFolders);

  readonly selectedEntitiesIds = this.dmsContentStore.selectedEntitiesIds;
  readonly selectedEntitiesIds$ = toObservable(this.selectedEntitiesIds);

  readonly unselectedEntitiesIds = this.dmsContentStore.unselectedEntitiesIds;

  readonly selectedFilesIds = this.dmsContentStore.selectedFilesIds;

  readonly selectedFoldersIds = this.dmsContentStore.selectedFoldersIds;

  readonly selectedFilesCount = this.dmsContentStore.selectedFilesCount;

  readonly contentPageNumber = computed(() => {
    const { limit, offset } = this.contentPagePagination();
    return offset / limit;
  });

  readonly contentPageCount = this.dmsContentStore.paginationTotal;
  readonly selectedItemsCount = computed(() => {
    const checkAll = this.dmsGlobalStore.checkAll();
    if (checkAll) {
      const total = this.contentPageCount();
      const unselectedEntitiesCount = this.unselectedEntitiesIds().length;
      return total - unselectedEntitiesCount;
    }
    return this.selectedEntitiesIds().length;
  });

  readonly hasAnySelection = computed(() => this.selectedItemsCount() > 0);

  readonly openedMenuFileId = this.dmsGlobalStore.openedMenuFileId;
  readonly filter$ = this.filterValues$.pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  ) as Observable<IrisFileFilterEntityI>;
  readonly isFilterActive$ = this.filter$.pipe(
    map(filesFilter => !!Object.keys(filesFilter || {}).length),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );
  readonly isFilterActive = toSignal(this.isFilterActive$);

  readonly isAnySelectedFileLocked = this.dmsContentStore.anySelectedFileLocked;

  readonly isAnyFolderOfSelectedFilesLocked = computed(() => {
    const selectedFiles = this.selectedFiles();

    return (selectedFiles ?? [])
      .map(file => this.isFileFolderLocked(file)())
      .some(Boolean);
  });

  readonly uploadingFiles = this.dmsGlobalStore.uploadingFiles;
  readonly uploadingFiles$ = toObservable(this.uploadingFiles);

  readonly contentLoading = computed(() =>
    this.dmsFoldersSandbox.isModalView()
      ? this.dmsModalFilesStore.loading()
      : this.dmsContentStore.contentLoading(),
  );
  readonly contentLoading$ = toObservable(this.contentLoading);

  readonly filesForPreivew = computed(() => {
    if (this.dmsFoldersSandbox.isModalView()) {
      return [];
    }
    return this.files();
  });

  private readonly filesMap$ = new Map<string, { file$: Observable<IrisDMSFileI>; completeSubject: Subject<void> }>();

  private readonly REMOVE_COMPLETED_FILE_DELAY = 30000;

  getFileById$(
    fileId: string,
    requestIfHistoryIsEmpty = false,
  ): Observable<IrisDMSFileI> {
    if (!this.filesMap$.has(fileId) || requestIfHistoryIsEmpty) {
      const completeSubject = new Subject<void>();
      const file = (this.dmsFoldersSandbox.isModalView() ? this.dmsModalFilesStore : this.dmsContentStore).getFileById(fileId);
      const file$ = toObservable(file, { injector: this.injector }).pipe(
        filter(() => !this.dmsContentStore.isFileLoading(fileId)),
        switchMap(file => this.contentLoading$.pipe(
          filter(contentLoading => !contentLoading),
          map(() => file),
        )),
        tap(file => {
          const shouldRequest = !file || (requestIfHistoryIsEmpty && (!file.history?.length));
          if (shouldRequest) {
            this.fetchFileById(fileId);
          }
        }),
        filter(Boolean),
        takeUntil(completeSubject),
      );
      if (this.filesMap$.has(fileId)) {
        this.filesMap$.get(fileId).file$.pipe(switchMap(() => file$));
      }
      this.filesMap$.set(fileId, { file$, completeSubject });
    }
    return this.filesMap$.get(fileId).file$;
  }

  getFileById(fileId: string): Signal<IrisDMSFileI> {
    const file$ = this.getFileById$(fileId);
    return toSignal(file$, { injector: this.injector });
  }

  getFileParentId$(fileId: string): Observable<string> {
    return this.getFileById$(fileId).pipe(
      take(1),
      map(file => file?.parentId),
    );
  }

  clearFilesMap(files: IrisDMSFileI[] = []): void {
    const filesIds = files.map(file => file.id);
    Array.from(this.filesMap$.keys()).filter(fileId => !filesIds.includes(fileId)).forEach(fileId => {
      const fileInfo = this.filesMap$.get(fileId);
      fileInfo.completeSubject.next();
      fileInfo.completeSubject.complete();
      this.filesMap$.delete(fileId);
    });
  }

  fileNamesExist(fileNames: string[], folderId: string): Observable<boolean> {
    return forkJoin(fileNames.map(name => this.filesService.getFileVersionNumber(folderId, name)))
      .pipe(
        map((versions) => {
          return versions.some(version => version > 0);
        }),
      );
  }

  fetchFileById(fileId: string): void {
    this.dmsContentStore.fetchFile$(fileId);
  }

  fetchFilesByIds(filesIds: string[]): void {
    this.dmsContentStore.fetchFilesByIds$(filesIds);
  }

  getPreviewType(file: IrisDataRepoFileI): string {
    return this.filesService.getPreviewType(file);
  }

  // id is file.id or file.thumbId, depends on the file.previewType
  getFilePreviewUrl(id: string): string {
    return this.filesService.getFilePreviewUrl(id);
  }

  deleteFile(fileId: string): void {
    this.dmsContentStore.deleteFile$(fileId);
  }

  deleteAttachment(fileId: string, attachmentId: string): void {
    this.dmsContentStore.deleteAttachment$({ fileId, attachmentId });
  }

  deleteFileVersion(fileId: string, versionId: string): void {
    this.dmsContentStore.deleteFileVersion$({ fileId, versionId });
  }

  getFileDownloadLink$ = (
    file: IrisDMSFileI,
    validForDays: number,
    permalink: boolean,
    isSilent: boolean,
    allowSingleDownload?: boolean,
  ): Observable<string> => {
    return this.filesService.share(file.id, validForDays, permalink, isSilent, allowSingleDownload).pipe(
      map((fileWithHash) => this.filesService.getLinkUrl(fileWithHash)),
      catchError(() => {
        return of(null);
      }),
    );
  };

  getFileSize(file: IrisDMSFileI): number {
    return file.fileSize || 0;
  }

  removeFiles(filesIds: string[]): void {
    this.dmsContentStore.removeFiles$({ filesIds, isFilterActive: this.isFilterActive() });
  }

  removeSelectedFoldersAndFiles(): void {
    this.dmsContentStore.removeSelectedFoldersAndFiles$();
  }

  getLanguagesNames(languages: string[] = []): string[] {
    return this.irisLanguageService.getLanguagesNames(languages).map(language => this.translateService.instant(language));
  }

  getFileLinkValidityValues(): ReturnType<IrisFilesService['getFileLinkValidityValues']> {
    return this.filesService.getFileLinkValidityValues();
  }

  getFileLinkLimitDownloadValues(): ReturnType<IrisFilesService['getFileLinkLimitDownloadValues']> {
    return this.filesService.getFileLinkLimitDownloadValues();
  }

  getFilterInProgressValues(): ReturnType<IrisDmsFilterService['getFilterInProgressValues']> {
    return this.dmsFilterService.getFilterInProgressValues();
  }

  updateEntity(entity: Partial<IrisDMSFolderOrFile>): void {
    this.dmsContentStore.updateEntity(entity);
  }

  updateFile(file: IrisDMSFileI): void {
    this.dmsContentStore.updateFileInfo$(file);
  }

  updateFileTags(fileId: string, tags: IrisDMSTagI[] = []): void {
    this.dmsContentStore.saveFileTags$({ fileId, tags });
  }

  updateFileCoordinates(fileId: string, coordinates: LatLngLiteral): void {
    this.dmsContentStore.updateFileCoordinates$({ fileId, coordinates });
  }

  updateFilesTags(files: IrisDMSFileI[]): void {
    this.dmsContentStore.updateFilesTags(files);
  }

  resetContent(onlyEntities = false): void {
    this.clearFilesMap();
    this.dmsContentStore.reset(onlyEntities);
  }

  resetContentAndReload(pageSize: number, orderBy: IrisQueryParamsOrderBy[] = []): void {
    this.resetContent();
    this.setContentPagePagination({ page: 0, pageSize, orderBy });
  }

  resetContentWithCurrentPagination(): void {
    const { limit: pageSize, orderBy } = this.contentPagePagination();
    this.resetContentAndReload(pageSize, orderBy);
  }

  resetFilter(): void {
    this.activeSidebarFilterService.resetValues();
  }

  restoreFile(fileId: string): void {
    this.dmsContentStore.restoreFile$(fileId);
  }

  setOpenedMenuFileId(fileId: string): void {
    this.dmsGlobalStore.setOpenedMenuFileId(fileId);
  }

  setUploadingFiles(files: IrisDMSFileI[]): void {
    if (!files.length) { return; }
    this.dmsGlobalStore.setUploadedFilesIds(files.map(file => new IrisDMSFile(file)));
  }

  translateFiles(language: string, filesIds: string[]): void {
    this.dmsContentStore.translateFile$({ language, filesIds });
  }

  isOfficeEditableDocument(mimeType: string): boolean {
    return this.filesService.isOfficeEditableDocument(mimeType);
  }

  isOfficeEditableExtension(fileName: string): boolean {
    return this.filesService.isOfficeEditableExtension(fileName);
  }

  isFileEditable(file: Partial<IrisDMSFileI>): boolean {
    return this.isOfficeEditableDocument(file?.mimeType) || this.isOfficeEditableExtension(file?.name);
  }

  setContentPagePagination(pagination: IrisPageChangedEvent): void {
    this.dmsContentStore.setPagination(pagination);
  }

  setContentPageCount(count: number): void {
    this.dmsContentStore.setPaginationTotal(count);
  }

  renameFile(file: IrisDMSFileI, fileName: string): void {
    this.dmsContentStore.renameFile(file, fileName);
  }

  isFileFolderLocked = (file: IrisDMSFileI): Signal<boolean> => computed(() => {
    const folder = this.dmsFoldersSandbox.getFolderById(file.parentId)();
    if (!folder) {
      this.dmsFoldersSandbox.getFolder(file.parentId);
    }
    return folder?.isLocked;
  });

  addUploadingFiles(files: (FileItem | File)[]): void {
    this.dmsGlobalStore.addUploadingFiles(files.map(file => {
      return {
        name: file instanceof FileItem ? file.file.name : file.name,
        state: FileUploadState.Uploading,
      };
    }));
  }

  updateUploadingFileState(name: string, newState: FileUploadState): void {
    this.dmsGlobalStore.updateUploadingFileState(name, newState);
    if (newState && newState !== FileUploadState.Uploading) {
      setTimeout(() => {
        this.removeUploadingFile(name);
      }, this.REMOVE_COMPLETED_FILE_DELAY);
    }
  }

  removeUploadingFile(name: string): void {
    this.dmsGlobalStore.removeUploadingFile(name);
  }

  addUploadingFileFolder(name: string, folderId: string): void {
    this.dmsGlobalStore.addUploadingFileFolder(name, folderId);
  }

  resetUploadingFiles(): void {
    this.dmsGlobalStore.resetUploadingFiles();
  }

  public openSearchPage(options?: UrlCreationOptions): void {
    this.dmsGlobalStore.selectDocumentsTab();
    void this.dmsRoutingService.openSearchViewPage(options);
  }

  public toggleSelectedFileQueryParam(fileId: string): void {
    toggleSelectedFileQueryParam(fileId, this.router, this.activatedRoute);
  }

  checkSameFileType(oldName: string, newName: string): Observable<string> {
    return this.filesService.checkSameFileType(oldName, newName).pipe(
      switchMap(() => of('')),
      catchError((error: Error) => of(error.message)),
    );
  }

  resetContentPagePagination(): void {
    this.dmsContentStore.resetPagination();
  }

  searchFilteredFiles(
    fileFilter: IrisQueryParams,
    offset: number,
    limit: number,
    orderBy: IrisQueryParamsOrderBy[] = [],
  ): Observable<DmsSearchResult> {
    const builder = this.dmsFilterService.dmsFilterProjectMeta.appendFilterQueryParamsFn(
      new IrisQueryParamsBuilder(fileFilter),
      [this.globalSandbox.currentProjectId()],
    );

    return this.filesService.searchFiles(builder.toStructure(), offset, limit, orderBy);
  }

  notifyElasticUnavailable(): void {
    this.toastService.error({ message: this.translateService.instant('error.ElasticNotAvailable') });
  }

  setFilteredSearchTotal(loadedItemsCount: number, isLastBlock: boolean): void {
    const total = isLastBlock
      ? loadedItemsCount
      : Math.max(this.contentTotal() ?? 0, loadedItemsCount);

    this.dmsContentStore.setPaginationTotal(total);
  }

  cacheEntities(entities: IrisDMSFolderOrFile[]): void {
    this.dmsContentStore.setEntities(entities);
  }

  getFilesNextVersionsByNames(names: string[], folderId: string): Observable<FileVersionByNameI> {
    const observables = names.map(name => this.filesService.getFileVersionNumber(folderId, name).pipe(
      map(fileVersion => ({ [name]: fileVersion + 1 })),
    ));

    return forkJoin(observables).pipe(
      map(filesVersionsByNames => Object.assign({}, ...filesVersionsByNames) as FileVersionByNameI),
    );
  }

  contentLoadingStart(): void {
    this.dmsContentStore.startContentLoading();
  }

  contentLoadingEnd(): void {
    this.dmsContentStore.finishContentLoading();
  }

  toggleEntitiesSelection(selectedIds: string[], deselectedIds: string[], checkAll = false): void {
    this.dmsContentStore.toggleEntitiesSelection(selectedIds, deselectedIds, checkAll);
  }

  selectEntities(ids: string[], replace = false): void {
    this.dmsContentStore.selectEntities(ids, replace);
  }

  deselectEntities(ids: string[], checkAll = false): void {
    this.dmsContentStore.deselectEntities(ids, checkAll);
  }

  resetSelection(): void {
    this.dmsContentStore.resetSelection();
  }

  reloadCurrentPage(): void {
    const { limit, orderBy } = this.contentPagePagination();
    const settingsOrderBy = this.dmsSettingsSandbox.foldersOrderBy();
    const pagination = {
      page: 0,
      pageSize: limit,
      orderBy: settingsOrderBy || orderBy,
      count: 0,
    };
    this.setContentPagePagination(pagination);
  }

  cancelUpload(uploadId: string, name: string): Observable<void> {
    return this.filesService.cancelUpload(uploadId, name);
  }

  isFileAttachedToForm$(fileId: string): Observable<boolean> {
    return this.filesService.isFileAttachedToForm(fileId);
  }

  removeRecentFiles(recentFilesIds: string[]): void {
    this.dmsContentStore.removeRecentFiles$(recentFilesIds);
  }

  getFilesLinkedToForms(folderId: string): Observable<IrisDMSFileI[]> {
    const params = this.dmsContentStore.bulkActionParams();
    return this.dmsBulkActionsService.checkLinked(folderId, params[0], params[1]).pipe(
      switchMap(fileIds => this.filesService.getFilesByIds(fileIds)),
      map(files => files.map(file => new IrisDMSFile(file))),
    );
  }

  getWopiUrl(fileId: string): string {
    return this.filesService.getWopiUrl(fileId, this.securityService.isAdmin);
  }

  isFileVisible(fileId: string): boolean {
    return !!this.dmsContentStore.getFileById(fileId)();
  }
}
